import { z } from 'zod';
/**
 * A table-side device: the camera that draws the overlay and pushes the
 * stream.
 *
 * The scoreboard is the control plane, so the device holds no opinion of its
 * own. It asks which match its table is playing, renders the OSD for it and
 * obeys the streaming switch. Everything it needs arrives on one payload, and
 * the credential that lets it push a stream never travels anywhere else.
 *
 * `venueId` is carried from the first version even though there is only one
 * venue today. A device is the first object in this system that genuinely
 * belongs to a club rather than to a match, and retro-fitting a tenant column
 * onto rows that are already deployed in the field is the expensive kind of
 * migration.
 */
export const DEVICE_TOKEN_BYTES = 32;
/** Header the device authenticates with, as `Authorization: Bearer <token>`. */
export const DEVICE_AUTH_SCHEME = 'Bearer';
const clubName = z.string().trim().max(120).nullable().optional();
export const DeviceModelSchema = z.string().regex(/^pi[1-9][0-9]*$/).max(16);
/** Unreported legacy devices retain their existing AI behavior until upgraded. */
export function deviceSupportsAi(model) {
    return model === null || model === 'pi5';
}
export const DeviceMetadataSchema = z.object({
    name: z.string().min(1).max(60).optional(),
    clubName,
    deviceModel: DeviceModelSchema.optional(),
}).strict();
export const CreateDeviceSchema = z.object({
    name: z.string().min(1).max(60),
    clubName,
    deviceModel: DeviceModelSchema.optional(),
    /**
     * Which club the table belongs to. Optional only when the caller belongs to
     * exactly one venue, where asking would be a question with one answer.
     */
    venueId: z.string().min(1).max(60).optional(),
});
export const UpdateDeviceSchema = z.object({
    name: z.string().min(1).max(60).optional(),
    clubName,
    /** The match this table is playing, or `null` to unbind. */
    matchId: z.string().min(1).nullable().optional(),
    /** @deprecated Legacy setting, accepted for compatibility; only the match switch controls streaming. */
    streaming: z.boolean().optional(),
    /**
     * RTMP ingest URL, e.g. `rtmp://a.rtmp.youtube.com/live2`. Readable back,
     * unlike the key: it identifies the platform and carries no secret.
     */
    rtmpUrl: z.string().max(500).nullable().optional(),
    /**
     * The stream key. Write-only: it is accepted here and handed to the device,
     * and is never included in any response a browser can reach. Send `null` to
     * clear it, or omit the field to leave it untouched.
     */
    streamKey: z.string().max(500).nullable().optional(),
});
export const DeviceSelfUpdateSchema = UpdateDeviceSchema.extend({
    deviceModel: DeviceModelSchema.optional(),
}).strict();
export const DEVICE_API = {
    devices: '/api/devices',
    device: (id) => `/api/devices/${id}`,
    /** Mint a replacement token; the previous one stops working immediately. */
    rotateToken: (id) => `/api/devices/${id}/token`,
    /** The device's own view, authenticated by its token rather than by id. */
    config: '/api/device/config',
    /** Device self-update: PATCH with device Bearer token to change own config. */
    selfUpdate: '/api/device/config',
    /** Matches visible to the device's venue, for binding dropdown. */
    selfMatches: '/api/device/matches',
    heartbeat: '/api/device/heartbeat',
    /** Authenticated, venue-scoped recently-seen devices. */
    active: '/api/devices/active',
    catalog: '/api/devices/catalog',
    /** Authenticated administrator settings; paired table binding is separately authorized. */
    activeDevice: (id) => `/api/devices/active/${id}`,
    /** Public: Pi self-registers on first boot — no credentials needed. */
    selfRegister: '/api/devices/register',
};
//# sourceMappingURL=devices.js.map